import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { FaSearch, FaPlus, FaList, FaTimes, FaEdit, FaTrash } from 'react-icons/fa';
import { showSuccess, showError } from '../../components/CustomAlert';
import { useConfirm } from '../../components/useConfirm';
import CustomConfirm from '../../components/CustomConfirm';
import { API_BASE_URL } from '../../utils/apiConfig';
import { hasPermission } from '../../utils/permissions';
const Container = styled.div`
  padding: 30px;
  background: #f8f9fa;
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 20px 15px;
  }
  
  @media (max-width: 480px) {
    padding: 15px 10px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
    margin-bottom: 25px;
  }
`;

const Title = styled.h1`
  color: #2c3e50;
  margin: 0;
  font-size: 1.7rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const IconButton = styled.button`
  background: transparent;
  border: 1px solid #e9ecef;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  color: #6c757d;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const AddButton = styled.button`
  background: rgb(51, 66, 147);;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 0.95rem;
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 15px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 25px 30px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  border-radius: 15px 15px 0 0;
`;

const ModalTitle = styled.h2`
  color: #2c3e50;
  margin: 0;
  font-size: 1.4rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #6c757d;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  
  &:hover {
    background: #e9ecef;
    color: #2c3e50;
  }
`;

const ModalBody = styled.div`
  padding: 30px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  font-size: 0.9rem;
  
  &:after {
    content: ${props => props.required ? '" *"' : '""'};
    color: #dc3545;
  }
`;

const Input = styled.input`
  padding: 12px 15px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ExampleText = styled.div`
  color: #6c757d;
  font-size: 0.8rem;
  margin-top: 5px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
`;

const CheckboxInput = styled.input`
  transform: scale(1.2);
`;

const CheckboxLabel = styled.label`
  color: #2c3e50;
  font-size: 0.9rem;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
`;

const SubmitButton = styled.button`
  background: rgb(51, 66, 147);;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  }
`;

const ResetButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    background: #5a6268;
    transform: translateY(-1px);
  }
`;

const AcademicYearContainer = styled.div`
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  
  @media (max-width: 768px) {
    border-radius: 12px;
  }
`;

const TableHeader = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 10px;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const TableCheckbox = styled.input`
  margin-right: 10px;
  transform: scale(1.2);
`;

const HeaderText = styled.span`
  font-weight: 600;
  color: #2c3e50;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const Thead = styled.thead`
  background: #2c3e50;
  color: white;
`;

const Th = styled.th`
  padding: 15px 20px;
  text-align: left;
  font-weight: 600;
  font-size: 0.9rem;
  border-right: 1px solid #34495e;
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 768px) {
    padding: 12px 15px;
    font-size: 0.85rem;
  }
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  &:nth-child(even) {
    background: #f8f9fa;
  }
  
  
  border-bottom: 1px solid #e9ecef;
`;

const Td = styled.td`
  padding: 15px 20px;
  color: #495057;
  font-size: 0.95rem;
  border-right: 1px solid #e9ecef;
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 768px) {
    padding: 12px 15px;
    font-size: 0.9rem;
  }
`;

const ActionButton = styled.button`
  background: ${props => props.danger ? '#dc3545' : 'rgb(51, 66, 147)'};
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  margin-right: 5px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 0.85rem;
    margin-right: 8px;
    min-width: 40px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    padding: 10px 14px;
    font-size: 0.9rem;
    margin-right: 10px;
    min-width: 44px;
    height: 40px;
  }
`;

const ActionCell = styled.td`
  width: 120px;
  text-align: center;
  
  @media (max-width: 768px) {
    width: auto;
    min-width: 140px;
    text-align: left;
    padding: 15px 20px;
  }
  
  @media (max-width: 480px) {
    min-width: 160px;
    padding: 15px 20px;
  }
`;

const StatusText = styled.span`
  color: ${props => props.current === 'true' || props.current === true ? '#28a745' : '#6c757d'};
  font-weight: ${props => props.current === 'true' || props.current === true ? '600' : '400'};
`;

const AcademicYear = () => {
  const { user } = useSelector((state) => state.auth);
  const canCreateProfile = hasPermission(user, 'profile_create');
  const canUpdateProfile = hasPermission(user, 'profile_update');
  const canDeleteProfile = hasPermission(user, 'profile_delete');

  const [selectedYears, setSelectedYears] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingYear, setEditingYear] = useState(null);
  const [academicYearData, setAcademicYearData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    academicYear: '',
    isCurrentYear: false
  });

  const { confirmState, showDeleteConfirm } = useConfirm();

  // Fetch academic years from API
  const fetchAcademicYears = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/profile/academic-years`);
      const data = await response.json();
      if (data.success) {
        setAcademicYearData(data.academicYears);
      }
    } catch (error) {
      console.error('Error fetching academic years:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAcademicYears();
  }, []);

  const openModal = (year = null) => {
    if (year) {
      setEditingYear(year);
      setFormData({
        academicYear: year.academic_year,
        isCurrentYear: year.is_current_year
      });
    } else {
      setEditingYear(null);
      setFormData({
        academicYear: '',
        isCurrentYear: false
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingYear(null);
    setFormData({
      academicYear: '',
      isCurrentYear: false
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.academicYear.trim()) {
      showError('Please enter an academic year');
      return;
    }

    setLoading(true);
    try {
      const url = editingYear 
        ? `${API_BASE_URL}/profile/academic-years/${editingYear.id}`
        : `${API_BASE_URL}/profile/academic-years`;
      
      const method = editingYear ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          academicYear: formData.academicYear,
          isCurrentYear: formData.isCurrentYear
        }),
      });

      const result = await response.json();

      if (response.ok) {
        showSuccess(result.message || 'Academic year updated successfully');
        closeModal();
        fetchAcademicYears();
      } else {
        showError(result.message || 'Failed to update academic year');
      }
    } catch (error) {
      showError('Error updating academic year');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await showDeleteConfirm(
      academicYearData.find(year => year.id === id)?.academic_year || 'Academic Year',
      'academic year'
    );

    if (!confirmed) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/profile/academic-years/${id}`, {
        method: 'DELETE',
      });

      const result = await response.json();

      if (response.ok) {
        showSuccess(result.message || 'Academic year deleted successfully');
        fetchAcademicYears();
      } else {
        showError(result.message || 'Failed to delete academic year');
      }
    } catch (error) {
      showError('Error deleting academic year');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (editingYear) {
      setFormData({
        academicYear: editingYear.academic_year,
        isCurrentYear: editingYear.is_current_year
      });
    } else {
      setFormData({
        academicYear: '',
        isCurrentYear: false
      });
    }
  };

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setSelectAll(isChecked);
    if (isChecked) {
      setSelectedYears(academicYearData.map(year => year.id));
    } else {
      setSelectedYears([]);
    }
  };

  const handleSelectYear = (yearId) => {
    setSelectedYears(prev => {
      if (prev.includes(yearId)) {
        return prev.filter(id => id !== yearId);
      } else {
        return [...prev, yearId];
      }
    });
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          Loading academic years...
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Academic Year</Title>
        <ActionButtons>
          {/* <IconButton>
            <FaSearch />
          </IconButton> */}
          {canCreateProfile && (
            <AddButton onClick={() => openModal()}>
              <FaPlus /> Add New Academic Year
            </AddButton>
          )}
          {/* <IconButton>
            <FaList />
          </IconButton> */}
        </ActionButtons>
      </Header>

      <AcademicYearContainer>
        <TableHeader>
          {/* <TableCheckbox
            type="checkbox"
            checked={selectAll}
            onChange={handleSelectAll}
          /> */}
          <HeaderText>Academic Year Records</HeaderText>
        </TableHeader>

        <Table style={{display: 'inline-table'}}>
          <Thead>
            <Tr>
              <Th style={{ width: '50px' }}></Th>
              <Th>Academic Year</Th>
              <Th>Is Current Year</Th>
              <Th style={{ width: '120px' }}>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {academicYearData.map((year) => (
              <Tr key={year.id}>
                <Td>
                  {/* <TableCheckbox
                    type="checkbox"
                    checked={selectedYears.includes(year.id)}
                    onChange={() => handleSelectYear(year.id)}
                  /> */}
                </Td>
                <Td>{year.academic_year}</Td>
                <Td>
                  <StatusText current={year.is_current_year}>
                    {year.is_current_year ? 'Yes' : 'No'}
                  </StatusText>
                </Td>
                <ActionCell>
                  {canUpdateProfile && (
                    <ActionButton onClick={() => openModal(year)}>
                      <FaEdit />
                    </ActionButton>
                  )}
                  {canDeleteProfile && (
                    <ActionButton danger onClick={() => handleDelete(year.id)}>
                      <FaTrash />
                    </ActionButton>
                  )}
                </ActionCell>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </AcademicYearContainer>

      {/* Custom Confirmation Dialog */}
      <CustomConfirm
        isOpen={confirmState.isOpen}
        onClose={confirmState.onCancel}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        details={confirmState.details}
        itemName={confirmState.itemName}
      />

      {showModal && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingYear ? 'Edit Academic Year' : 'Add Academic Year'}</ModalTitle>
              <CloseButton onClick={closeModal}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label required>Academic Year</Label>
                  <Input
                    type="text"
                    name="academicYear"
                    value={formData.academicYear}
                    onChange={handleInputChange}
                    placeholder="e.g., 2023-2024"
                    required
                  />
                  <ExampleText>Example: 2023-2024, 2024-2025</ExampleText>
                </FormGroup>

                <CheckboxContainer>
                  <CheckboxInput
                    type="checkbox"
                    name="isCurrentYear"
                    checked={formData.isCurrentYear}
                    onChange={handleInputChange}
                  />
                  <CheckboxLabel>Set as Current Academic Year</CheckboxLabel>
                </CheckboxContainer>

                <ButtonGroup>
                  <ResetButton type="button" onClick={handleReset}>
                    Reset
                  </ResetButton>
                  {(editingYear ? canUpdateProfile : canCreateProfile) && (
                    <SubmitButton type="submit" disabled={loading}>
                      {loading ? 'Saving...' : (editingYear ? 'Update' : 'Add')}
                    </SubmitButton>
                  )}
                </ButtonGroup>
              </form>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default AcademicYear; 